import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Navigate } from 'react-router-dom';
import { accountService } from '@/services/account-service';
import { useCurrentUser } from '@/hooks/use-current-user';
import { 
  Header, 
  Field, 
  FieldLabel,
  CheckboxField,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Badge,
  Button,
  Spinner,
  SearchInput
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { 
  Building2,
  AlertCircle
} from '@/lib/icons';

export function AccountsPage() {
  const navigate = useNavigate();
  const { setShowPageHeader } = usePageContext();
  const { data: currentAdmin } = useCurrentUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;

  // Affiliate users should not have access to Accounts
  const isAffiliate = currentAdmin?.type === 'prosci_admin' && currentAdmin?.level === 'affiliate';

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  // Fetch accounts
  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts', searchQuery, activeOnly, currentPage],
    queryFn: () => accountService.listAccounts({
      q: searchQuery,
      active_licenses_only: activeOnly,
      limit: pageSize,
      skip: currentPage * pageSize
    }),
    staleTime: 30000, // 30 seconds
  });

  // Redirect affiliate users to Users page (must be after all hooks)
  if (isAffiliate) {
    return <Navigate to="/users" replace />;
  }

  // Extract accounts and pagination info
  const accounts = data?.data?.map(item => ({
    ...item.attributes,
    id: item.id
  })) || [];
  const totalCount = data?.meta?.total_count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleRowClick = (accountId: string) => {
    navigate(`/accounts/${accountId}`);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActiveOnly(false);
    setCurrentPage(0);
  };

  const hasFilters = Boolean(searchQuery || activeOnly);

  if (error) {
    return (
      <>
        <Header 
          title="Accounts" 
          size="small"
        />
        <ContentContainer>
          <Empty>
            <EmptyIcon variant="destructive">
              <AlertCircle />
            </EmptyIcon>
            <EmptyTitle>Error loading accounts</EmptyTitle>
            <EmptyDescription>
              {error instanceof Error ? error.message : 'Failed to load accounts. Please try again.'}
            </EmptyDescription>
          </Empty>
        </ContentContainer>
      </>
    );
  }

  return (
    <>
      <Header 
        title="Accounts" 
        size="small"
      />
      
      {/* Filters */}
      <div className="bg-white border-b border-accent-sand">
        <div className="max-w-[1160px] px-6 py-4 mx-auto">
          <form onSubmit={handleSearch}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search Accounts */}
              <SearchInput
                id="search"
                label="Search Accounts"
                placeholder="Search by account name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(0);
                }}
              />

              {/* Active Licenses Filter */}
              <Field>
                <FieldLabel>Filter</FieldLabel>
                <CheckboxField
                  checked={activeOnly}
                  onCheckedChange={(checked) => {
                    setActiveOnly(!!checked);
                    setCurrentPage(0);
                  }}
                  label="Show only accounts with active licenses"
                />
              </Field>
            </div>
            
            {hasFilters && (
              <div className="flex items-center justify-end mt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>

      <ContentContainer>
        {/* Accounts table */}
          {isLoading ? (
            <Empty>
              <EmptyIcon>
                <Spinner size="small" />
              </EmptyIcon>
              <EmptyTitle>Loading accounts</EmptyTitle>
              <EmptyDescription>
                Please wait while we fetch the accounts...
              </EmptyDescription>
            </Empty>
          ) : accounts.length === 0 ? (
            <Empty>
              <EmptyIcon>
                <Building2 />
              </EmptyIcon>
              <EmptyTitle>No accounts found</EmptyTitle>
              <EmptyDescription>
                Try adjusting your search or filters to find accounts.
              </EmptyDescription>
              {hasFilters && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              )}
            </Empty>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Account List</CardTitle>
                <CardDescription>
                  Complete list of all accounts in your organization.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account Name</TableHead>
                      <TableHead>License Admins</TableHead>
                      <TableHead className="text-center">Users</TableHead>
                      <TableHead className="text-center">Products</TableHead>
                      <TableHead className="text-center">SAML Login</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.map((account) => (
                      <TableRow 
                        key={account.id} 
                        onClick={() => handleRowClick(account.id)}
                        className="cursor-pointer"
                      >
                        <TableCell>
                          <div className="font-medium">
                            {account.name}
                          </div>
                          {account.registration_slug && (
                            <div className="text-sm text-muted-foreground">
                              {account.registration_slug}
                            </div>
                          )} 
                        </TableCell>
                        <TableCell>
                          {account.license_admins.length > 0 
                            ? account.license_admins.join(', ')
                            : <span className="text-muted-foreground italic">None Assigned</span>
                          }
                        </TableCell>
                        <TableCell className="text-center">
                          {account.total_users}
                        </TableCell>
                        <TableCell className="text-center">
                          {account.total_products}
                        </TableCell>
                        <TableCell className="text-center">
                          {account.uses_saml_login ? (
                            <Badge variant="success" tint>
                              Enabled
                            </Badge>
                          ) : (
                            <Badge variant="warning" tint>
                              Disabled
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 border-t pt-4 flex flex-col md:flex-row items-center justify-between gap-8">
                    <p className="text-sm text-gray-600">
                      Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount} accounts
                    </p>
                    <Pagination className="w-auto mx-0">
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(p => Math.max(0, p - 1));
                            }}
                            className={currentPage === 0 ? 'pointer-events-none opacity-50' : ''}
                          />
                        </PaginationItem>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i;
                          } else if (currentPage <= 2) {
                            pageNum = i;
                          } else if (currentPage >= totalPages - 3) {
                            pageNum = totalPages - 5 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCurrentPage(pageNum);
                                }}
                                isActive={pageNum === currentPage}
                              >
                                {pageNum + 1}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        })}
                        {totalPages > 5 && currentPage < totalPages - 3 && (
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                        )}
                        <PaginationItem>
                          <PaginationNext
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(p => Math.min(totalPages - 1, p + 1));
                            }}
                            className={currentPage === totalPages - 1 ? 'pointer-events-none opacity-50' : ''}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
      </ContentContainer>
    </>
  );
}